import { motion } from "framer-motion";
import { useState } from "react";

const Survey = () => {
  const [formData, setFormData] = useState({
    school: "",
    email: "",
    phone: "",
    currentActivities: "",
    futureActivities: "",
  });

  const [status, setStatus] = useState<null | "success" | "error">(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      setStatus(null);
      const response = await fetch(
        "https://www.dieceznytabor.sk/admin/public/api/survey-mail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Chyba pri odosielaní formulára");
      }

      setStatus("success");
    } catch (error) {
      console.error("Odosielanie zlyhalo:", error);
      setStatus("error");
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    exit: { opacity: 0, y: -50, transition: { duration: 0.8 } },
  };

  const inputVariants = {
    focus: {
      scale: 1.02,
      borderColor: "#FF5F00",
      boxShadow: "0 0 10px rgba(255, 95, 0, 0.4)",
      transition: { duration: 0.3 },
    },
  };

  const logoVariants = {
    animate: {
      y: [0, -10, 0],
      scale: [1, 1.03, 1],
      transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
    },
  };

  return (
    <section className="bg-cream text-navy relative py-8 md:py-12 lg:py-20 overflow-hidden min-h-[auto]">
      <motion.h2
        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-8 md:mb-12 lg:mb-16 uppercase tracking-tight text-primary drop-shadow-2xl"
        style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        Prieskum
      </motion.h2>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 min-h-[auto]">
          {/* Left side with logo/text */}
          <motion.div
            className="w-full lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            <div className="text-center">
              <motion.div
                className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-bold uppercase text-primary opacity-70 mb-6 md:mb-8 lg:mb-10"
                style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
                animate={{ rotate: [0, 3, -3, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                Tvoj názor
              </motion.div>
              <motion.img
                src="./logo.png"
                alt="Logo"
                className="h-32 sm:h-40 md:h-48 lg:h-56 drop-shadow-xl mx-auto"
                variants={logoVariants}
                animate="animate"
              />
            </div>
          </motion.div>

          {/* Right side with form */}
          <motion.div
            className="w-full lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            <motion.div
              variants={formVariants}
              initial="hidden"
              animate="visible"
              className="bg-white p-6 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-3xl shadow-xl border-2 sm:border-4 border-secondary w-full max-w-md sm:max-w-lg lg:max-w-2xl relative"
            >
              <motion.div
                className="absolute -top-4 -left-4 sm:-top-6 sm:-left-6 md:-top-8 md:-left-8 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 md:-bottom-8 md:-right-8 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-secondary rounded-full"
                animate={{ rotate: -360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              />

              {/* Form Fields */}
              <motion.div variants={inputVariants}>
                <label
                  className="block text-lg sm:text-xl md:text-2xl font-bold uppercase text-navy mb-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Názov školy
                </label>
                <motion.input
                  type="text"
                  name="school"
                  value={formData.school}
                  onChange={handleChange}
                  className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-navy rounded-lg sm:rounded-xl focus:outline-none bg-cream/90 text-base sm:text-lg"
                  whileFocus="focus"
                />
              </motion.div>

              <motion.div variants={inputVariants} className="mt-6 sm:mt-8">
                <label
                  className="block text-lg sm:text-xl md:text-2xl font-bold uppercase text-navy mb-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Mailový kontakt
                </label>
                <motion.input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-navy rounded-lg sm:rounded-xl focus:outline-none bg-cream/90 text-base sm:text-lg"
                  whileFocus="focus"
                />
              </motion.div>

              <motion.div variants={inputVariants} className="mt-6 sm:mt-8">
                <label
                  className="block text-lg sm:text-xl md:text-2xl font-bold uppercase text-navy mb-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Mobilné číslo
                </label>
                <motion.input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-navy rounded-lg sm:rounded-xl focus:outline-none bg-cream/90 text-base sm:text-lg"
                  whileFocus="focus"
                />
              </motion.div>

              <motion.div variants={inputVariants} className="mt-6 sm:mt-8">
                <label
                  className="block text-lg sm:text-xl md:text-2xl font-bold uppercase text-navy mb-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Aké aktivity robievame / zúčastňujeme sa
                </label>
                <motion.textarea
                  name="currentActivities"
                  value={formData.currentActivities}
                  onChange={handleChange}
                  className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-navy rounded-lg sm:rounded-xl focus:outline-none bg-cream/90 text-base sm:text-lg min-h-[100px] sm:min-h-[120px]"
                  whileFocus="focus"
                />
              </motion.div>

              <motion.div variants={inputVariants} className="mt-6 sm:mt-8">
                <label
                  className="block text-lg sm:text-xl md:text-2xl font-bold uppercase text-navy mb-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Aké aktivity by sme si predstavovali do budúcna
                </label>
                <motion.textarea
                  name="futureActivities"
                  value={formData.futureActivities}
                  onChange={handleChange}
                  className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-navy rounded-lg sm:rounded-xl focus:outline-none bg-cream/90 text-base sm:text-lg min-h-[100px] sm:min-h-[120px]"
                  whileFocus="focus"
                />
              </motion.div>

              <motion.button
                onClick={handleSubmit}
                className="bg-primary text-cream px-8 sm:px-10 md:px-12 py-4 sm:py-5 md:py-6 rounded-lg sm:rounded-xl uppercase font-bold shadow-xl w-full mt-8 sm:mt-10 md:mt-12 text-lg sm:text-xl"
                whileHover={{ scale: 1.05, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                Odoslať
              </motion.button>

              {status === "success" && (
                <p className="mt-4 text-center text-green-700 font-bold text-sm sm:text-base">
                  Formulár bol úspešne odoslaný!
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 text-center text-red-700 font-bold text-sm sm:text-base">
                  Nastala chyba pri odosielaní.
                </p>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Survey;
